/**
 * ConsentBadge — GDPR consent status chip + attestation dialog.
 *
 * Shows the patient's consent state at a glance:
 *   green  — recording + AI analysis both granted
 *   amber  — partially granted
 *   grey   — nothing granted
 * Clicking opens ConsentDialog, where the therapist can attest that the
 * patient gave (or withdrew) consent on paper/verbally
 * (channel: dashboard_attested) and review the append-only history.
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, ShieldAlert, ShieldOff, X } from "lucide-react";
import { useToast } from "./Toast";
import {
  useConsents,
  CONSENT_PURPOSES,
  type ConsentAction,
  type ConsentPurpose,
  type UseConsentsResult,
} from "../hooks/useConsents";
import "./ConsentBadge.css";

const PURPOSE_LABEL_KEYS: Record<ConsentPurpose, string> = {
  recording: "consent.purposeRecording",
  ai_analysis: "consent.purposeAiAnalysis",
  wearables: "consent.purposeWearables",
};

const PURPOSE_DESC_KEYS: Record<ConsentPurpose, string> = {
  recording: "consent.purposeRecordingDesc",
  ai_analysis: "consent.purposeAiAnalysisDesc",
  wearables: "consent.purposeWearablesDesc",
};

type BadgeLevel = "loading" | "error" | "full" | "partial" | "none";

const LEVEL_LABEL_KEYS: Record<BadgeLevel, string> = {
  loading: "consent.loading",
  error: "consent.statusUnavailable",
  full: "consent.badgeFull",
  partial: "consent.badgePartial",
  none: "consent.badgeNone",
};

const LEVEL_ICONS: Record<BadgeLevel, React.ComponentType<{ size?: number }>> = {
  loading: ShieldOff,
  error: ShieldAlert,
  full: ShieldCheck,
  partial: ShieldAlert,
  none: ShieldOff,
};

type Props = {
  patient: string;
  patientName?: string;
};

export default function ConsentBadge({ patient, patientName }: Props) {
  const { t } = useTranslation();
  const consents = useConsents(patient);
  const [open, setOpen] = useState(false);

  let level: BadgeLevel;
  if (consents.error) {
    level = "error";
  } else if (!consents.data) {
    level = "loading";
  } else {
    const s = consents.data.state;
    if (s.recording && s.ai_analysis) level = "full";
    else if (s.recording || s.ai_analysis || s.wearables) level = "partial";
    else level = "none";
  }

  const Icon = LEVEL_ICONS[level];

  return (
    <>
      <button
        type="button"
        className={`consent-badge consent-badge--${level}`}
        onClick={() => setOpen(true)}
        title={consents.error ?? t("consent.dialogTitle")}
        aria-label={t("consent.dialogTitle")}
      >
        <Icon size={13} />
        <span>{t(LEVEL_LABEL_KEYS[level])}</span>
      </button>
      {open && (
        <ConsentDialog
          consents={consents}
          patientName={patientName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ============================================
// ConsentDialog
// ============================================

type DialogProps = {
  consents: UseConsentsResult;
  patientName?: string;
  onClose: () => void;
};

type PendingAttestation = {
  purpose: ConsentPurpose;
  action: ConsentAction;
};

export function ConsentDialog({ consents, patientName, onClose }: DialogProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [pending, setPending] = useState<PendingAttestation | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attestError, setAttestError] = useState<string | null>(null);

  // Close on Escape (matches ConfirmDialog behavior)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const startAttestation = (purpose: ConsentPurpose, action: ConsentAction) => {
    setPending({ purpose, action });
    setConfirmed(false);
    setAttestError(null);
  };

  const cancelAttestation = () => {
    setPending(null);
    setConfirmed(false);
    setAttestError(null);
  };

  const handleConfirm = async () => {
    if (!pending || !confirmed || submitting) return;
    setSubmitting(true);
    setAttestError(null);
    try {
      await consents.attest(pending.purpose, pending.action);
      toast.info(t("consent.attestSuccess"));
      setPending(null);
      setConfirmed(false);
    } catch (e) {
      console.error("[ConsentDialog] attest failed:", e);
      setAttestError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(i18n.language === "hu" ? "hu-HU" : "en-GB");
  };

  const data = consents.data;
  const name = patientName || t("consent.thePatient");

  return (
    <div className="consent-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="consent-dialog"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t("consent.dialogTitle")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="consent-dialog-header">
          <div>
            <div className="consent-dialog-title">{t("consent.dialogTitle")}</div>
            <div className="consent-dialog-subtitle">
              {patientName ? `${patientName} · ` : ""}
              {data
                ? t("consent.policyVersion", { version: data.policy_version })
                : null}
            </div>
          </div>
          <button
            type="button"
            className="consent-close"
            onClick={onClose}
            aria-label={t("consent.close")}
          >
            <X size={16} />
          </button>
        </div>

        {consents.error && (
          <div className="consent-error">
            {t("consent.loadError")}: {consents.error}
            <button
              type="button"
              className="consent-btn consent-btn--secondary"
              onClick={() => void consents.refresh()}
            >
              {t("consent.retry")}
            </button>
          </div>
        )}

        {!consents.error && !data && (
          <div className="consent-loading">{t("consent.loading")}</div>
        )}

        {data && (
          <>
            <div className="consent-purposes">
              {CONSENT_PURPOSES.map((purpose) => {
                const granted = data.state[purpose];
                return (
                  <div key={purpose} className="consent-purpose">
                    <div className="consent-purpose-info">
                      <div className="consent-purpose-name">
                        {t(PURPOSE_LABEL_KEYS[purpose])}
                      </div>
                      <div className="consent-purpose-desc">
                        {t(PURPOSE_DESC_KEYS[purpose])}
                      </div>
                    </div>
                    <span
                      className={`consent-status ${
                        granted ? "consent-status--granted" : "consent-status--missing"
                      }`}
                    >
                      {granted
                        ? t("consent.statusGranted")
                        : t("consent.statusNotGranted")}
                    </span>
                    <button
                      type="button"
                      className={`consent-btn ${
                        granted ? "consent-btn--danger" : "consent-btn--primary"
                      }`}
                      disabled={submitting}
                      onClick={() =>
                        startAttestation(purpose, granted ? "revoked" : "granted")
                      }
                    >
                      {granted ? t("consent.attestRevoke") : t("consent.attestGrant")}
                    </button>
                  </div>
                );
              })}
            </div>

            {pending && (
              <div className="consent-confirm">
                <div className="consent-confirm-title">
                  {pending.action === "granted"
                    ? t("consent.confirmGrantTitle")
                    : t("consent.confirmRevokeTitle")}
                </div>
                <div className="consent-confirm-text">
                  {pending.action === "granted"
                    ? t("consent.confirmGrantText", {
                        name,
                        purpose: t(PURPOSE_LABEL_KEYS[pending.purpose]),
                      })
                    : t("consent.confirmRevokeText", {
                        name,
                        purpose: t(PURPOSE_LABEL_KEYS[pending.purpose]),
                      })}
                </div>
                <label className="consent-confirm-check">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span>
                    {pending.action === "granted"
                      ? t("consent.confirmCheckboxGrant")
                      : t("consent.confirmCheckboxRevoke")}
                  </span>
                </label>
                {attestError && (
                  <div className="consent-error">
                    {t("consent.attestError")}: {attestError}
                  </div>
                )}
                <div className="consent-confirm-actions">
                  <button
                    type="button"
                    className="consent-btn consent-btn--secondary"
                    onClick={cancelAttestation}
                    disabled={submitting}
                  >
                    {t("consent.cancel")}
                  </button>
                  <button
                    type="button"
                    className={`consent-btn ${
                      pending.action === "granted"
                        ? "consent-btn--primary"
                        : "consent-btn--danger"
                    }`}
                    onClick={() => void handleConfirm()}
                    disabled={!confirmed || submitting}
                  >
                    {submitting
                      ? t("consent.confirmSubmitting")
                      : t("consent.confirmSubmit")}
                  </button>
                </div>
              </div>
            )}

            <div className="consent-history">
              <div className="consent-history-title">{t("consent.historyTitle")}</div>
              {data.history.length === 0 ? (
                <div className="consent-history-empty">{t("consent.historyEmpty")}</div>
              ) : (
                <ul className="consent-history-list">
                  {data.history.map((ev, i) => (
                    <li key={i} className="consent-history-item">
                      <span className="consent-history-date">
                        {formatDate(ev.created_at)}
                      </span>
                      <span className="consent-history-purpose">
                        {(CONSENT_PURPOSES as readonly string[]).includes(ev.purpose)
                          ? t(PURPOSE_LABEL_KEYS[ev.purpose as ConsentPurpose])
                          : ev.purpose}
                      </span>
                      <span
                        className={`consent-history-action consent-history-action--${ev.action}`}
                      >
                        {ev.action === "granted"
                          ? t("consent.actionGranted")
                          : t("consent.actionRevoked")}
                      </span>
                      <span className="consent-history-channel">
                        {ev.channel === "mobile_app"
                          ? t("consent.channelMobileApp")
                          : t("consent.channelDashboard")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
